/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { ICON_SIZE } from "../Icon";
import { BEM } from "@adapptio/lib-components";
import { BreadcrumbsItem } from "./BreadcrumbsItem";
import { BreadcrumbsCollapsed } from "./BreadcrumbsCollapsed";
import { IBreadcrumbsItem } from "./BreadcrumbsTypes";
import { BreadcrumbsSeparator } from "./BreadcrumbsSeparator";

const MAX_VISIBLE_CRUMBS_LENGTH_DEFAULT = 3;

export interface IBreadcrumbsProps {
	ariaLabel?: string;
	className?: string;
	items?: IBreadcrumbsItem[];
	separatorSize?: ICON_SIZE;
	allowBreak?: boolean;
	maxVisibleCrumbs?: number;
}

function insertSeparators(items, className, separatorSize) {
	return items.reduce((acc, current, index) => {
		if (index < items.length - 1) {
			acc = acc.concat(
				current,
				<BreadcrumbsSeparator
					key={`separator-${index}`}
					className={className}
					separatorSize={separatorSize}
				/>
			);
		} else {
			acc.push(current);
		}

		return acc;
	}, []);
}

export const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({
	children,
	className,
	ariaLabel,
	items,
	separatorSize,
	allowBreak,
	maxVisibleCrumbs
}) => {
	const baseClass = BEM("edt-breadcrumbs", null, {
		"allow-break": allowBreak
	});

	const childItems = React.Children.toArray(children);

	const _maxCrumbsLength = maxVisibleCrumbs ? maxVisibleCrumbs : MAX_VISIBLE_CRUMBS_LENGTH_DEFAULT;

	const visibleItems = items.slice(-_maxCrumbsLength);
	const collapsedItems = items.slice(0, -_maxCrumbsLength);

	const crumbs = useMemo(() => {
		if (childItems.length > 0) {
			return childItems.map((child, index) => (
				<li className={baseClass.element("list-item").toClassName()} key={`child-${index}`}>
					{child}
				</li>
			));
		}

		return visibleItems.map((item, index) => (
			<li className={baseClass.element("list-item").toClassName()} key={`child-${index}`}>
				<BreadcrumbsItem
					key={`breadcrumbs-item-${item.label}`}
					onClick={item.onClick}
					tooltip={item.label}
					fullWidth={index === visibleItems.length - 1}
				>
					{item.label}
				</BreadcrumbsItem>
			</li>
		));
	}, [ childItems, visibleItems ]);

	if (collapsedItems.length > 0) {
		crumbs.unshift(<BreadcrumbsCollapsed key="breadcrumbs-collapsed" items={collapsedItems} />);
	}

	return (
		<nav className={`${baseClass.toClassName()} ${className}`} aria-label={ariaLabel}>
			<ol className={baseClass.element("list").toClassName()}>
				{insertSeparators(crumbs, baseClass.element("list-item").toClassName(), separatorSize)}
			</ol>
		</nav>
	);
};

Breadcrumbs.defaultProps = {
	ariaLabel: "breadcrumb",
	items: [],
	separatorSize: ICON_SIZE.SMALL,
	className: "",
	allowBreak: false
};
